/**
 * Challenge reviews tab — rating summary card with distribution bars,
 * write/edit review button, paginated reviews list.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { MessageCircle, Star, Pencil } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

import { AvatarWithBadge, getBadgeDisplay } from '@turnifly/shared/ui'
import { Button } from '@turnifly/shared/ui'
import { useChallengeReviewsList } from '@turnifly/shared/hooks'
import { ChallengeReviewSheet } from './challenge-review-sheet'
import type { ReviewsSummary, UserReview, ReviewItem } from '@turnifly/shared/types'

// ── Props ────────────────────────────────────────────────────

interface ChallengeReviewsTabProps {
  challengeId: string
  reviewsSummary: ReviewsSummary
  userReview: UserReview | null
  canReview: boolean
}

// ── Rating bar ───────────────────────────────────────────────

function RatingBar({ star, count, maxCount }: {
  star: number
  count: number
  maxCount: number
}): React.ReactElement {
  const width = maxCount > 0 ? (count / maxCount) * 100 : 0
  return (
    <div className="flex items-center gap-2">
      <span className="w-3 text-right text-[11px] tabular-nums text-[var(--color-text-hint)]/60">
        {star}
      </span>
      <Star size={9} className="shrink-0 fill-[#FFD700] text-[#FFD700]" />
      <div className="h-[6px] flex-1 overflow-hidden rounded-full bg-white/[0.04]">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${width}%` }}
          transition={{ duration: 0.6, ease: 'easeOut', delay: (5 - star) * 0.08 }}
          className="h-full rounded-full bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-accent)]"
        />
      </div>
      <span className="w-5 text-right text-[10px] tabular-nums text-[var(--color-text-hint)]/40">
        {count}
      </span>
    </div>
  )
}

// ── Review item row ──────────────────────────────────────────

function ReviewItemRow({ review, index }: {
  review: ReviewItem
  index: number
}): React.ReactElement {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, delay: index * 0.04 }}
      className="flex gap-3 px-4 py-3"
    >
      <AvatarWithBadge
        src={review.avatar_url}
        fallback={review.full_name}
        badge={getBadgeDisplay(false, review.badge_type)}
        size="sm"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-[13px] font-semibold text-white">
            {review.full_name}
          </span>
          <span className="shrink-0 text-[10px] text-[var(--color-text-hint)]/40">
            {formatDistanceToNow(new Date(review.created_at), { addSuffix: true })}
          </span>
        </div>
        <div className="mt-0.5 flex gap-0.5">
          {[1, 2, 3, 4, 5].map((s) => (
            <Star
              key={s}
              size={11}
              className={
                s <= review.rating
                  ? 'fill-[#FFD700] text-[#FFD700]'
                  : 'text-[var(--color-text-hint)]/15'
              }
            />
          ))}
        </div>
        {review.text && (
          <p className="mt-1.5 whitespace-pre-line text-[12px] leading-relaxed text-white/70">
            {review.text}
          </p>
        )}
      </div>
    </motion.div>
  )
}

// ── Main component ───────────────────────────────────────────

export function ChallengeReviewsTab({
  challengeId,
  reviewsSummary,
  userReview,
  canReview,
}: ChallengeReviewsTabProps): React.ReactElement {
  const { t } = useTranslation()
  const [isSheetOpen, setSheetOpen] = useState(false)
  const [page, setPage] = useState(0)
  const { data: reviews, isFetching } = useChallengeReviewsList(challengeId, page)

  const { avg_rating, total_count, distribution } = reviewsSummary
  const maxDist = Math.max(...distribution, 1)

  if (total_count === 0 && !canReview) {
    return (
      <div className="py-16 text-center">
        <MessageCircle
          size={40}
          className="mx-auto mb-3 text-[var(--color-text-hint)]/15"
        />
        <p className="text-[13px] text-[var(--color-text-hint)]">
          {t('challenges.profile.noReviews')}
        </p>
      </div>
    )
  }

  return (
    <div className="pb-6">
      {/* Rating summary */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mx-4 mb-4 mt-3 rounded-2xl bg-white/[0.04] p-5"
      >
        <div className="flex items-center gap-6">
          <div className="min-w-[60px] shrink-0 text-center">
            <p className="text-[42px] font-bold tabular-nums leading-none text-white">
              {avg_rating?.toFixed(1) ?? '—'}
            </p>
            <div className="mt-1.5 flex justify-center gap-0.5">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star
                  key={s}
                  size={13}
                  className={
                    s <= Math.round(avg_rating ?? 0)
                      ? 'fill-[#FFD700] text-[#FFD700]'
                      : 'text-[var(--color-text-hint)]/15'
                  }
                />
              ))}
            </div>
            <p className="mt-1 text-[10px] text-[var(--color-text-hint)]/50">
              {t('challenges.profile.reviewsCount', { count: total_count })}
            </p>
          </div>

          <div className="flex-1 space-y-1.5">
            {[5, 4, 3, 2, 1].map((star) => (
              <RatingBar
                key={star}
                star={star}
                count={distribution[star - 1] ?? 0}
                maxCount={maxDist}
              />
            ))}
          </div>
        </div>
      </motion.div>

      {/* Write/edit review */}
      {canReview && (
        <div className="mb-4 px-4">
          <Button
            variant="glass"
            fullWidth
            onClick={() => setSheetOpen(true)}
            className="gap-2"
          >
            <Pencil size={14} />
            {userReview
              ? t('challenges.profile.editReview')
              : t('challenges.profile.writeReview')}
          </Button>
        </div>
      )}

      {/* Reviews list */}
      {reviews && reviews.length > 0 && (
        <div className="divide-y divide-white/[0.04]">
          {reviews.map((review, i) => (
            <ReviewItemRow key={review.id} review={review} index={i} />
          ))}
        </div>
      )}

      {reviews && reviews.length >= 20 && (
        <div className="mt-3 px-4">
          <Button
            variant="glass"
            size="sm"
            fullWidth
            isLoading={isFetching}
            onClick={() => setPage((p) => p + 1)}
          >
            {t('challenges.profile.loadMore')}
          </Button>
        </div>
      )}

      {total_count === 0 && canReview && (
        <div className="py-6 text-center">
          <p className="text-[12px] text-[var(--color-text-hint)]/40">
            {t('challenges.profile.noReviews')}
          </p>
        </div>
      )}

      <ChallengeReviewSheet
        isOpen={isSheetOpen}
        onClose={() => setSheetOpen(false)}
        challengeId={challengeId}
        existingReview={userReview}
      />
    </div>
  )
}
